/** Parent-facing Year 7 path for The Tiffin Girls' School. */

export const TIFFIN_GIRLS_JOURNEY = {
  schoolId: 1,
  schoolName: "The Tiffin Girls' School",
  kicker: "School guide",
  title: "What to do for Tiffin Girls'",
  lead:
    "A short parent guide: what the school is, how the inner and designated areas work, how to apply, exams, results, how places are ordered, and how this school sits next to nearby grammars.",
  heroStats: [
    { label: "GCSE rank", value: "#4" },
    { label: "Gender", value: "Girls" },
    { label: "Catchment", value: "Inner + Outer" },
    { label: "Oxbridge offers", value: "15" },
    { label: "Area", value: "Kingston" },
  ],
  catchmentVisual: {
    kind: "map",
    aria: "Tiffin Girls catchment map",
    caption: "",
    labels: {
      school: "Tiffin Girls'",
      layers: {
        1: {
          title: "Priority area 1 · Inner",
          detail: "Named electoral wards closest to the school",
        },
        2: {
          title: "Priority area 2 · Designated / outer",
          detail: "Wider postcode districts with catchment priority",
        },
      },
      outside:
        "Outside these areas usually means a lower chance of an offer. Recent years have filled from inside the designated area.",
    },
  },
  sources: [
    {
      label: "Year 7 admissions",
      href: "https://www.tiffingirls.org/admissions/year-7/",
    },
    {
      label: "Admissions home",
      href: "https://www.tiffingirls.org/admissions/",
    },
    {
      label: "Sixth form / Oxbridge note",
      href: "https://www.tiffingirls.org/academic/sixth-form/",
    },
  ],
  steps: [
    {
      id: "overview",
      title: "1. Overview",
      summary: "What Tiffin Girls' is, in one minute",
      paragraphs: [
        "The Tiffin Girls' School is a selective state grammar school for girls in Kingston upon Thames. It is not fee-paying. Girls take a two-stage entrance process in Year 6 for a place in Year 7.",
        "There are 180 Year 7 places in the published policy year we hold. Places go to girls who pass the tests and then meet the school's admission rules, which also look at where the family lives.",
        "Unlike an open school with no map, Tiffin Girls' uses two geographic bands: an inner area of electoral wards, and a wider designated / outer area of postcode districts.",
      ],
    },
    {
      id: "catchment",
      title: "2. Catchment check",
      summary: "Inner wards, then the wider designated area",
      paragraphs: [
        "Think of catchment here as two bands, not one simple circle. The inner area is a list of electoral wards close to the school. The wider designated area is a longer list of postcode districts. Girls living in these areas are given priority once they have passed the tests.",
        "Marks still matter. After the entrance tests, eligible girls are ordered by score — often called rank order or marks order. Living in the right band gets her into the priority geography; a higher score usually means she is higher up that list.",
        "In recent years the school has said that places have been offered inside the designated area. Use the map below, then confirm your address against the school’s published lists.",
        "When does she need to be in catchment? Tiffin Girls' uses the permanent home address for catchment priority. Treat the CAF closing date at the end of October as the key date: the address on that form should be the permanent home you want the school to use. Moving after that date can change whether she still counts as inner, designated, or outside — check the current policy if you plan a move.",
      ],
      example: {
        title: "Example",
        kind: "inner_outer",
        body:
          "Girl A lives in an inner ward, Girl B in the wider designated area, and Girl C outside both. After the tests, A and B sit in the priority geography bands; C is usually behind them. Inside a band, a higher score still moves a girl up the list.",
      },
      showCatchmentVisual: true,
      points: [
        "Priority area 1 = inner wards (about 60 places can sit here)",
        "Priority area 2 = designated / outer postcodes",
        "Outside those areas: lower chance of an offer",
        "Home address for catchment: permanent home on the CAF (end of October)",
      ],
    },
    {
      id: "apply",
      title: "3. How to apply",
      summary: "Register for the tests, then name the school on the CAF",
      paragraphs: [
        "Dates below are for Year 7 entry in September 2027 (from Tiffin Girls' published key dates). Always re-check the school site if you are applying for a later year.",
        "You need the school's entrance-test registration and your home local authority CAF. Passing the tests is not enough if Tiffin Girls' is not named on the CAF in preference order.",
        "For 2027 entry, Stage One registration opened on 2 June 2026 and closed at 12:00 noon on 1 September 2026. Stage One is held on 1–2 October 2026; invited girls sit Stage Two on 7 November 2026.",
        "The CAF deadline is 31 October 2026. Put the school in your true order of preference.",
      ],
      timeline: [
        { when: "2 June 2026", what: "Stage One registration opens (2027 entry)" },
        { when: "1 Sept 2026, noon", what: "Stage One registration closes" },
        { when: "1–2 Oct 2026", what: "Stage One entrance test" },
        { when: "~14 Oct 2026", what: "Stage Two invitations notified" },
        { when: "31 Oct 2026", what: "CAF deadline" },
        { when: "7 Nov 2026", what: "Stage Two for invited girls" },
        { when: "1 March 2027", what: "National Offer Day" },
      ],
    },
    {
      id: "exams",
      title: "4. Exams and preparation",
      summary: "Two stages: multiple choice, then written papers",
      paragraphs: [
        "Stage 1 uses GL Assessment-style English and Maths multiple-choice papers. Girls who do well enough are invited to stage 2.",
        "Stage 2 is the school's own written English and Maths. That is where more of the final ranking is decided.",
        "These papers are for Tiffin Girls’ only. Sitting them does not register her for Tiffin School (boys), for the Sutton SET schools, or for any other grammar. If you also want those schools, you need their own registration and tests as well as naming them on the CAF.",
        "Prepare with steady English and Maths practice under timed conditions. Use the school's current admissions pages for paper style and access arrangements.",
      ],
      points: [
        "Stage 1 — GL English and Maths (multiple choice)",
        "Stage 2 — written English and Maths (Tiffin Girls’ only)",
        "Does not unlock other grammars’ entrance tests",
      ],
    },
    {
      id: "place-order",
      title: "5. How places are ordered",
      summary: "The offer list, step by step",
      paragraphs: [
        "After the tests, Tiffin Girls' follows a published place order. Disadvantaged children come first. Then geography and test score work together.",
        "In simple terms: looked-after children and other published priority groups first; then eligible girls in the catchment bands, ordered by entrance-test score. The inner area can take up to about 60 places; the wider designated area covers the broader postcode list.",
        "So catchment answers “which band are we in?” Rank order answers “where does she sit inside that band?”",
      ],
      placeOrder: [
        { title: "EHCP naming the school", detail: "Handled before the normal list" },
        { title: "Looked-after / previously looked-after", detail: "Highest published priority group" },
        { title: "Other published priority routes", detail: "For example free school meals or service children in catchment, as the policy sets out" },
        { title: "Inner area by marks", detail: "Eligible girls in the ward band, highest score first (up to about 60 places)" },
        { title: "Designated / outer area by marks", detail: "Eligible girls in the wider postcode band, highest score first" },
      ],
    },
    {
      id: "results",
      title: "6. Results",
      summary: "Where Tiffin Girls' sits among grammars",
      paragraphs: [
        "On GCSEs, Tiffin Girls' sits near the top of the grammar range — well above the average Attainment 8 score, and close to the highest schools we hold.",
        "At A level the school is again in the high group: an A-grade average, clearly above the grammar mean.",
        "The school has also published Oxbridge offers on its sixth-form pages. Treat that as a school-published figure, separate from the DfE destinations release.",
      ],
      facts: [
        { label: "GCSE rank", value: "#4" },
        { label: "GCSE Attainment 8", value: "85.3" },
        { label: "A level average", value: "A" },
        { label: "Oxbridge offers", value: "15" },
      ],
    },
    {
      id: "allocation",
      title: "7. Places and demand",
      summary: "First exam funnel, then offer day",
      paragraphs: [
        "The first exam is where most families fall away. In the school's own admissions information for recent years, around 1,500 girls sat stage 1. Only about 470–475 reached stage 2. The published Year 7 intake is 180 places.",
        "So stage 1 is a wide sitting and a sharp cut. Reaching stage 2 still leaves several girls for every place.",
        "On National Offer Day, first preferences are also well above the number of offers. Passing both stages and living in the right band still have to line up.",
      ],
      facts: [
        { label: "Sat stage 1 (2026)", value: "1,468" },
        { label: "Reached stage 2", value: "474" },
        { label: "First preferences", value: "445" },
        { label: "Offers", value: "189" },
      ],
    },
    {
      id: "nearby",
      title: "8. Nearby schools",
      summary: "How Tiffin Girls' sits beside local grammars",
      paragraphs: [
        "The closest grammar neighbour is Tiffin School for boys, under a mile and a half away. Further out you will also see Nonsuch and Sutton Grammar on many Kingston / Sutton shortlists.",
        "On GCSE Attainment 8, Tiffin Girls' sits above those nearby peers in the latest set we hold. Tiffin School for boys publishes a higher Oxbridge offer count. Use both facts as context, not as a single “best school” score.",
      ],
      nearby: [
        { name: "Tiffin School", detail: "About 1.5 miles · boys' grammar · lower GCSE Attainment 8, higher published Oxbridge count" },
        { name: "Nonsuch High School for Girls", detail: "About 9–10 miles · girls' grammar · strong results, a little below Tiffin Girls'" },
        { name: "Sutton Grammar School", detail: "About 10 miles · boys' grammar · often compared on the same Sutton / Kingston shortlist" },
      ],
    },
    {
      id: "admission",
      title: "9. Offer day",
      summary: "From the list to March",
      paragraphs: [
        "National Offer Day is in early March. By then the school has already applied the place order above.",
        "If you are unsure whether your address is inner or designated / outer, check it on the SchoolCatch map and confirm against the school's published lists for your entry year.",
      ],
    },
  ],
};
